/**
 * <age-verification>
 *
 * The 18+ confirmation in the cart summary. Checkout stays blocked until its
 * checkbox is checked. The checkbox starts unchecked on every page load.
 *
 * Loaded from a theme asset on every page (snippets/scripts.liquid), so the
 * element and the checkout guard also work when the cart summary arrives through
 * a section morph, such as the first product in an empty cart drawer or on /cart:
 * inline <script> tags inserted by morph never execute.
 */
class AgeVerification extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #walletObserver;

  connectedCallback() {
    this.checkbox = this.querySelector('input[type="checkbox"]');
    this.message = this.querySelector('.cart__age-verification-message');
    if (!this.checkbox) return;

    this.checkbox.addEventListener('change', this.#onChange);

    // A cart morph runs after ThemeEvents.cartUpdate and rebuilds the wallet buttons without `inert`
    if (this.parentElement) {
      this.#walletObserver = new MutationObserver(this.#lockWallets);
      this.#walletObserver.observe(this.parentElement, { subtree: true, childList: true, attributeFilter: ['inert'] });
    }
    this.#lockWallets();
  }

  disconnectedCallback() {
    this.checkbox?.removeEventListener('change', this.#onChange);
    this.#walletObserver?.disconnect();
  }

  get verified() {
    return Boolean(this.checkbox?.checked);
  }

  showMessage() {
    if (!this.message) return;
    this.message.hidden = false;
    this.message.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  #onChange = () => {
    if (this.checkbox.checked && this.message) this.message.hidden = true;
    this.#lockWallets();
  };

  /**
   * A wallet button can render in an iframe, where neither the click guard nor
   * `pointer-events` reach a keyboard user. Its container stays clickable, so a
   * click still shows the message.
   */
  #lockWallets = () => {
    const wallets = this.parentElement?.querySelector(':scope > .cart__ctas .additional-checkout-buttons');
    for (const wallet of /** @type {HTMLElement[]} */ ([...(wallets?.children ?? [])])) {
      if (wallet.inert === this.verified) wallet.inert = !this.verified;
    }
  };
}

if (!customElements.get('age-verification')) {
  customElements.define('age-verification', AgeVerification);
}

/**
 * The age verification of the cart that holds `element`: the checkout button and
 * the cart form share a cart container with it.
 * @param {Element} element
 * @returns {AgeVerification | null}
 */
function ageVerificationFor(element) {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const ageVerification = node.querySelector('age-verification');
    if (ageVerification) return /** @type {AgeVerification} */ (ageVerification);
  }
  return null;
}

/**
 * @param {Event} event
 * @param {Element} element
 */
function blockUnverifiedCheckout(event, element) {
  const ageVerification = ageVerificationFor(element);
  if (!ageVerification || ageVerification.verified) return;

  event.preventDefault();
  event.stopPropagation();
  ageVerification.showMessage();
}

// Capture phase, so the guard runs before the cart form submits or an accelerated checkout button starts.
document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;
    const control = event.target.closest('#checkout, .cart__checkout-button, .additional-checkout-buttons');
    if (control) blockUnverifiedCheckout(event, control);
  },
  true
);

document.addEventListener(
  'submit',
  (event) => {
    if (event.target instanceof HTMLFormElement && event.target.id === 'cart-form') {
      blockUnverifiedCheckout(event, event.target);
    }
  },
  true
);
